import { Button, Table, TablePaginationConfig, DatePicker, Space } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import api, { Payment } from 'api';
import {
	formatNumber,
	getCellValue,
	getDateValue,
	getShowTotal,
} from 'utils/tableUtils';
import moment from 'moment';
import styles from './SalesDetails.module.less';
import { downloadFileByObjectURL } from 'utils/fileUtils';
const { RangePicker } = DatePicker;

type ParamsProps = {
	projectId: string;
};
const getColumns = (t: TFunction) => [
	{
		title: t('financePage.SalesDetailsTab.Price'),
		key: 'price',
		render: (record: Payment) =>
			getCellValue(
				t('common.currency', {
					val: formatNumber(record.price || 0),
				}),
			),
	},
	{
		title: t('financePage.SalesDetailsTab.Revenue'),
		key: 'revenue',
		render: (record: Payment) =>
			getCellValue(
				t('common.currency', {
					val: formatNumber(
						(record.price || 0) - (record.commissionCollected || 0),
					),
				}),
			),
	},
	{
		title: t('financePage.SalesDetailsTab.Code'),
		key: 'code',
		width: 300,
		render: (record: Payment) => getCellValue(record.redemptionCode),
	},
	{
		title: t('financePage.SalesDetailsTab.Purchaser'),
		key: 'purchaser',
		width: 200,
		render: (record: Payment) => getCellValue(record.purchaser),
	},
	{
		title: t('financePage.SalesDetailsTab.PurchaseTime'),
		key: 'purchaseTime',
		width: 200,
		render: (record: Payment) => getDateValue(record.createdDate),
	},
];
export default function SalesDetails() {
	const { t } = useTranslation();
	const { projectId } = useParams<ParamsProps>();
	const [filterStartDate, setFilterStartDate] = useState<
		moment.Moment | undefined
	>();
	const [filterEndDate, setFilterEndDate] = useState<
		moment.Moment | undefined
	>();
	const [salesDetails, setSalesDetails] = useState<Payment[]>();
	const columns = useMemo(() => getColumns(t), [t]);
	const [isLoading, setIsLoading] = useState(false);
	const showTotal = useCallback(
		(total: number, range: [number, number]) => getShowTotal(total, range, t),
		[t],
	);
	const [pagination, setPagination] = useState<TablePaginationConfig>({
		current: 1,
		pageSize: 10,
		size: 'default',
		showQuickJumper: true,
		showTotal,
		showSizeChanger: true,
	});
	const handleTableChange = async (tablePagination: TablePaginationConfig) => {
		setIsLoading(true);

		api
			.getProjectPayments(
				parseInt(projectId),
				filterStartDate?.toDate(),
				filterEndDate?.toDate(),
				false,
				false,
				tablePagination.pageSize as number,
				(tablePagination.current as number) - 1,
			)
			.then((result) => {
				setIsLoading(false);
				setSalesDetails(result.items || []);
				setPagination({
					...pagination,
					...tablePagination,
					total: result.totalCount || 0,
				});
			});
	};
	useEffect(() => {
		handleTableChange(pagination);
	}, []);
	const exportSalesDetails = () => {
		api
			.exportProjectPayments(
				parseInt(projectId),
				filterStartDate?.toDate(),
				filterEndDate?.toDate(),
				true,
				false,
				pagination.total as number,
				0,
			)
			.then((result) => {
				downloadFileByObjectURL(
					URL.createObjectURL(result.data),
					result.fileName,
				);
			});
	};
	return (
		<>
			<Space direction="vertical" size={18}>
				<div>
					<Space direction="horizontal" size={12}>
						<RangePicker
							format={'MM/DD/YYYY'}
							className={styles.rangePicker}
							onChange={(value) => {
								if (value && value[0]) {
									setFilterStartDate(moment(value[0] as moment.Moment));
									if (value[1]) {
										setFilterEndDate(value[1] as moment.Moment);
									}
								}
							}}
						/>
						<Button
							type="default"
							onClick={() => handleTableChange(pagination)}
						>
							{t('common.filter')}
						</Button>
					</Space>
					<Button
						className={styles.exportButton + ' color-dark'}
						type="default"
						onClick={exportSalesDetails}
					>
						{t('common.exportCsv')}
					</Button>
				</div>
				<Table
					className="table"
					size="middle"
					columns={columns}
					rowKey={(record) => `${record.createdDate?.toString()}_${record.id}`}
					dataSource={salesDetails}
					pagination={pagination}
					loading={isLoading}
					onChange={handleTableChange}
					tableLayout="fixed"
					data-testid="salesDetailsTable"
				/>
			</Space>
		</>
	);
}
